import { type CSSProperties, useEffect, useRef } from 'react';
import { autoUpdate, type ReferenceElement } from '@floating-ui/dom';
import { motion, useReducedMotion } from 'motion/react';
import { CircleStop } from 'lucide-react';
import { OwnedOverlayPortal } from './owned-overlay-portal';

// Rendered inside the page iframe: no app stylesheet, so everything is inline
// and hard-coded to the design's palette (Figma "UI Frame Loading").
const FONT_FAMILY = 'system-ui, sans-serif';
const PRIMARY = '#6366f1';
const FOREGROUND = '#fafafa';
const FRAME_FILL = 'rgba(99,102,241,0.4)';
const FRAME_BORDER = 2;
const FRAME_RADIUS = 2;
/** Label height (24px) + the 2px gap between the label and the frame. */
const LABEL_OFFSET = 26;
/** The shimmer band is wider than the frame so its diagonal fully crosses it. */
const SHIMMER_WIDTH = '130%';
/** Travel from fully off the left edge to fully off the right edge, in the band's own width. */
const SHIMMER_TRAVEL = `${Math.round((230 / 130) * 100)}%`;

// Values from the Figma motion export (node 25189:1820): the band sweeps
// across during the first ~30% of a 2s timeline, then rests until the loop
// restarts.
const SHIMMER_TRANSITION = {
  x: { duration: 2, times: [0, 0.299, 1], ease: 'linear' as const, repeat: Infinity },
};

const DISPLAY_CONTENTS: CSSProperties = { display: 'contents' };

const labelStyle: CSSProperties = {
  position: 'absolute',
  top: -LABEL_OFFSET,
  left: 0,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  height: 24,
  padding: 4,
  borderRadius: 8,
  background: PRIMARY,
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
  whiteSpace: 'nowrap',
};

const labelTextStyle: CSSProperties = {
  padding: '0 4px',
  fontFamily: FONT_FAMILY,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  color: FOREGROUND,
};

const frameStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  boxSizing: 'border-box',
  overflow: 'hidden',
  borderRadius: FRAME_RADIUS,
  border: `${FRAME_BORDER}px solid ${PRIMARY}`,
  background: FRAME_FILL,
};

const shimmerStyle: CSSProperties = {
  position: 'absolute',
  top: -FRAME_BORDER,
  bottom: -FRAME_BORDER,
  left: `-${SHIMMER_WIDTH}`,
  width: SHIMMER_WIDTH,
  borderRadius: FRAME_RADIUS,
  backgroundImage:
    'linear-gradient(135deg, rgba(255,255,255,0) 25%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 75%)',
  willChange: 'transform',
};

export type SavingOverlayProps = {
  /** The element whose edit is being persisted. */
  target: Element;
  doc: Document;
};

/**
 * "Applying changes..." feedback drawn over an element while its inline text
 * edit is being saved: a label pill above the element and a tinted frame with
 * a looping diagonal shimmer over it (Figma "UI Frame Loading").
 *
 * Portaled into the iframe body as a fixed box synced to the target's rect
 * (same mechanics as the badges), so it escapes any `overflow: hidden`
 * ancestor and follows the page while it scrolls.
 */
export function SavingOverlay({ target, doc }: SavingOverlayProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const win = doc.defaultView;

    const update = () => {
      const rect = target.getBoundingClientRect();
      node.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
      node.style.width = `${rect.width}px`;
      node.style.height = `${rect.height}px`;

      if (!win) return;

      // `visibility` is inherited, so the computed value already reflects ancestors.
      node.style.visibility = win.getComputedStyle(target).visibility;

      // `opacity` is NOT inherited but visually multiplies down the tree —
      // walk up and multiply to get the effective opacity of the target.
      let opacity = 1;
      let current: Element | null = target;
      while (current) {
        const o = parseFloat(win.getComputedStyle(current).opacity);
        if (!isNaN(o)) opacity *= o;
        current = current.parentElement;
      }
      node.style.opacity = String(opacity);
    };

    update();
    return autoUpdate(target as ReferenceElement, node, update, {
      animationFrame: true,
    });
  }, [target, doc]);

  if (!doc.body) return null;

  return (
    <OwnedOverlayPortal host={doc.body} containerStyle={DISPLAY_CONTENTS}>
      <div
        ref={ref}
        data-tinct-overlay=""
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          // Above the block rings (99997) and badges (99999): the saving
          // state is the one thing the user must see on this element.
          zIndex: 100000,
          pointerEvents: 'none',
          willChange: 'transform',
        }}
      >
        <div style={frameStyle}>
          {/* Reduced motion: the tinted frame + label already convey the
              state, so the sweep is simply not rendered. */}
          {!reducedMotion && (
            <motion.div
              style={shimmerStyle}
              initial={{ x: 0 }}
              animate={{ x: [0, SHIMMER_TRAVEL, SHIMMER_TRAVEL] }}
              transition={SHIMMER_TRANSITION}
            />
          )}
        </div>
        <div style={labelStyle}>
          <span style={labelTextStyle}>Applying changes...</span>
          <CircleStop size={16} color={FOREGROUND} strokeWidth={1.5} aria-hidden="true" />
        </div>
      </div>
    </OwnedOverlayPortal>
  );
}
